import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

/**
 * DirectMapRedirectPage
 *
 * This page is the target of every "Direct Map Link" QR code.
 * When a user scans the QR code their device opens this page, which:
 *   1. Calls the backend to log the scan (same schema as location QR scans)
 *   2. Fetches the actual MappedIn directMapUrl for this location
 *   3. Immediately redirects the browser to that MappedIn URL
 *
 * Route: /direct-map-redirect/:locationId?lang=en
 */
const DirectMapRedirectPage = () => {
  const { locationId } = useParams();
  const [searchParams] = useSearchParams();
  const language = searchParams.get("lang") || "en";

  const [status, setStatus] = useState("loading"); // 'loading' | 'redirecting' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!locationId || hasRunRef.current) return;
    hasRunRef.current = true;

    const run = async () => {
      try {
        setStatus("loading");

        // Ask the backend to log the scan and return the MappedIn URL
        const response = await axios.get(
          `/api/wayfinder/direct-map-redirect/${locationId}`,
          { params: { lang: language } }
        );

        if (!response.data.success || !response.data.directMapUrl) {
          throw new Error(
            response.data.message || "Direct map URL not available"
          );
        }

        const { directMapUrl } = response.data;

        setStatus("redirecting");

        // Small delay so the user sees the "Redirecting…" message
        setTimeout(() => {
          window.location.href = directMapUrl;
        }, 500);
      } catch (err) {
        console.error("❌ DirectMapRedirectPage error:", err);
        setErrorMsg(
          err.response?.data?.message ||
            err.message ||
            "Failed to load directions"
        );
        setStatus("error");
      }
    };

    run();
  }, [locationId, language]);

  // ── Styles ──────────────────────────────────────────────────────────────────
  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    fontFamily: "Arial, sans-serif",
    backgroundColor: "#f5f5f5",
    padding: "20px",
    textAlign: "center",
  };

  const spinnerStyle = {
    width: "48px",
    height: "48px",
    border: "5px solid #e0e0e0",
    borderTop: "5px solid #1976d2",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginBottom: "24px",
  };

  const titleStyle = {
    fontSize: "20px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "8px",
  };

  const subtitleStyle = {
    fontSize: "14px",
    color: "#666",
  };

  const errorIconStyle = {
    fontSize: "48px",
    marginBottom: "16px",
  };

  const errorTitleStyle = {
    fontSize: "20px",
    fontWeight: 600,
    color: "#e53935",
    marginBottom: "8px",
  };

  const errorMsgStyle = {
    fontSize: "14px",
    color: "#666",
    maxWidth: "320px",
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (status === "error") {
    return (
      <div style={containerStyle}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={errorIconStyle}>⚠️</div>
        <div style={errorTitleStyle}>Unable to Load Directions</div>
        <div style={errorMsgStyle}>{errorMsg}</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={spinnerStyle} />
      <div style={titleStyle}>
        {status === "redirecting" ? "Opening Map…" : "Loading Directions…"}
      </div>
      <div style={subtitleStyle}>
        {status === "redirecting"
          ? "You will be redirected to the interactive map shortly."
          : "Please wait while we prepare your directions."}
      </div>
    </div>
  );
};

export default DirectMapRedirectPage;
